import { I18n } from "@assets/strings";
import { useObservable } from "micro-observables";
import React, { JSX } from "react";
import styled from "styled-components";
import { useServices } from "@core/servicesContext";
import { useAsync, useEffectAsync } from "@utils/hooks";
import { PrimaryBold } from "@ui/components/containers/primary";
import { InfoBulle } from "@ui/components/infoBulle";
import { DatePicker } from "@ui/components/inputs/datePicker";
import { Selecter } from "@ui/components/inputs/selecter";
import { row, stack } from "@ui/components/layout";
import { mobile } from "@ui/styles/utils";
import { useApplicationEditorController } from "@ui/pages/application/applicationEditorPage/applicationEditorController/applicationEditorControllerContext";

export const StudyChoiceSectionEditor: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ ...htmlProps }) => {
	const controller = useApplicationEditorController();
	const { credit, degreeId, degrees, levelId, levels, credits, desiredStartDate } = useObservable(
		controller.application
	);

	const { applicationService } = useServices();

	if (!applicationService) {
		return null;
	}

	const { run, loading: isProgramLoading } = useAsync(
		async (degree_level: string) => {
			controller.setDegrees([]);
			controller.setCredits();
			const result = await applicationService.getDegreeProgram(degree_level);
			controller.setDegrees(result);
		},
		[levelId]
	);

	useEffectAsync(async () => {
		if (levelId) {
			const result = await applicationService.getDegreeProgram(levelId);
			controller.setDegrees(result);
		}
	}, [levelId, degreeId]);

	const getFormItem = (field: "degree_level" | "degree_program" | "credit_points" | "desired_date"): JSX.Element => (
		<InfoEdition>
			<PrimaryBold>
				{I18n.t(`application.form.${field}.label`)}
				<span style={{ color: "red" }}>*</span>
			</PrimaryBold>
			<InfoBulle text={I18n.t(`application.form.${field}.tooltip`)} />
		</InfoEdition>
	);

	return (
		<Container {...htmlProps}>
			<>
				{levels && (
					<FormField>
						{getFormItem("degree_level")}
						<TypeSelecter
							missing={false}
							placeholder={I18n.t(`application.form.degree_level.placeholder`)}
							value={levelId}
							onChange={(value) => {
								controller.setLevelId(value);
								run(value);
							}}
							options={levels?.map((level) => ({
								label: level.name,
								value: level.id,
							}))}
							customStyle={true}
						/>
					</FormField>
				)}
				{degrees && (
					<FormField>
						{getFormItem("degree_program")}

						<TypeSelecter
							missing={false}
							placeholder={I18n.t(`application.form.degree_program.placeholder`)}
							value={degreeId}
							onChange={(value) => {
								controller.setDegreeId(value);
							}}
							options={degrees?.map((program) => ({ label: program.name, value: program.id }))}
							customStyle={true}
						/>
					</FormField>
				)}
				<StyledForm>
					{credits?.length ? (
						<StyledCreditField>
							{getFormItem("credit_points")}
							<TypeSelecter
								placeholder={I18n.t(`application.form.credit_points.placeholder`)}
								value={credit}
								onChange={(value) => controller.setCreditPoint(value)}
								options={credits?.map((credit) => ({ label: credit.toString(), value: credit.toString() }))}
								customStyle={true}
								loading={isProgramLoading}
							/>
						</StyledCreditField>
					) : null}
					<StyledDateField>
						{getFormItem("desired_date")}
						<DatePicker
							customStyle={true}
							value={desiredStartDate}
							onChange={(date) => controller.setDesiredStartDate(date)}
							minYear={2022}
							maxYear={2030}
						/>
					</StyledDateField>
				</StyledForm>
			</>
		</Container>
	);
};

const Container = styled.div`
	position: relative;
	margin-top: 100px;
	display: flex;
	flex-direction: column;
	gap: 20px;

	@media ${mobile} {
		margin-top: 180px;
	}
`;

const InfoEdition = styled.div`
	${row(10, "flex-start", "stretch")};
`;

const FormField = styled.div`
	${stack(10, "flex-start", "stretch")};
	width: 85%;
	@media ${mobile} {
		width: 100%;
	}
`;

const StyledCreditField = styled(FormField)`
	width: 25%;
	@media ${mobile} {
		width: 100%;
	}
`;

const StyledDateField = styled(FormField)`
	// ${stack(10, "flex-start")};
	width: 40%;
	@media ${mobile} {
		width: 100%;
	}
`;

const TypeSelecter = styled(Selecter)<{ missing?: boolean }>`
	select {
		${({ missing, theme }) => missing && `border-color: ${theme.colors.red}`};
	}
`;

const StyledForm = styled.div`
	display: flex;
	gap: 30px;
	align-items: center;

	@media ${mobile} {
		flex-direction: column;
		gap: 20px;
	}
`;
